import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import { PublicLayout } from "../components/layout/PublicLayout";
import { HomePage } from "../pages/home/HomePage";
import { LoginPage } from "../pages/auth/LoginPage";
import { RegisterPage } from "../pages/auth/RegisterPage";
import { AccountLayout } from "../pages/account/AccountLayout";
import { AccountPage } from "../pages/account/AccountPage";
import { OrdersPage } from "../pages/account/OrdersPage";
import { OrderDetailsPage } from "../pages/account/OrderDetailsPage";
import { WishlistPage } from "../pages/account/WishlistPage";
import { WalletPage } from "../pages/account/WalletPage";
import { SettingsPage } from "../pages/account/SettingsPage";
import { CategoryPage } from "../pages/category/CategoryPage";
import { SearchPage } from "../pages/search/SearchPage";
import { ProductPage } from "../pages/product/ProductPage";
import { CartPage } from "../pages/cart/CartPage";
import { CheckoutPage } from "../pages/checkout/CheckoutPage";
import { SafepayResultPage } from "../pages/payment/SafepayResultPage";
import { ErrorPage } from "../pages/ErrorPage";
import { ProtectedRoute } from "./ProtectedRoute";

// These are all low-traffic, non-shopping-flow pages (legal, contact, static
// info) — code-split so the initial bundle only pays for the pages most
// visitors actually load: home, category, product, cart, checkout.
const AboutPage = lazy(() => import("../pages/about/AboutPage").then((m) => ({ default: m.AboutPage })));
const ReturnsPage = lazy(() => import("../pages/returns/ReturnsPage").then((m) => ({ default: m.ReturnsPage })));
const ContactPage = lazy(() => import("../pages/contact/ContactPage").then((m) => ({ default: m.ContactPage })));
const PrivacyPolicyPage = lazy(() =>
  import("../pages/legal/PrivacyPolicyPage").then((m) => ({ default: m.PrivacyPolicyPage }))
);
const TermsPage = lazy(() => import("../pages/legal/TermsPage").then((m) => ({ default: m.TermsPage })));
const TrackOrderPage = lazy(() => import("../pages/track/TrackOrderPage").then((m) => ({ default: m.TrackOrderPage })));
const NotFoundPage = lazy(() => import("../pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })));
const OffersPage = lazy(() => import("../pages/offers/OffersPage").then((m) => ({ default: m.OffersPage })));
const BundlesPage = lazy(() => import("../pages/bundles/BundlesPage").then((m) => ({ default: m.BundlesPage })));
const BundleDetailPage = lazy(() =>
  import("../pages/bundles/BundleDetailPage").then((m) => ({ default: m.BundleDetailPage }))
);

function PageFallback() {
  return <div className="min-h-[50vh]" />;
}

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

// Admin subtree is code-split into its own chunk so customer bundles
// never pay for admin-only data tables and forms.
const AdminShell = lazy(() => import("../components/layout/AdminShell").then((m) => ({ default: m.AdminShell })));
const AdminOverviewPage = lazy(() => import("../pages/admin/AdminOverviewPage").then((m) => ({ default: m.AdminOverviewPage })));
const AdminProductsPage = lazy(() => import("../pages/admin/AdminProductsPage").then((m) => ({ default: m.AdminProductsPage })));
const AdminInventoryPage = lazy(() => import("../pages/admin/AdminInventoryPage").then((m) => ({ default: m.AdminInventoryPage })));
const AdminCategoriesPage = lazy(() => import("../pages/admin/AdminCategoriesPage").then((m) => ({ default: m.AdminCategoriesPage })));
const AdminOrdersPage = lazy(() => import("../pages/admin/AdminOrdersPage").then((m) => ({ default: m.AdminOrdersPage })));
const AdminInvoicePage = lazy(() => import("../pages/admin/AdminInvoicePage").then((m) => ({ default: m.AdminInvoicePage })));
const AdminBusinessReportPage = lazy(() =>
  import("../pages/admin/AdminBusinessReportPage").then((m) => ({ default: m.AdminBusinessReportPage }))
);
const AdminReturnsPage = lazy(() => import("../pages/admin/AdminReturnsPage").then((m) => ({ default: m.AdminReturnsPage })));
const AdminDiscountsPage = lazy(() => import("../pages/admin/AdminDiscountsPage").then((m) => ({ default: m.AdminDiscountsPage })));
const AdminReviewsPage = lazy(() => import("../pages/admin/AdminReviewsPage").then((m) => ({ default: m.AdminReviewsPage })));
const AdminBundlesPage = lazy(() => import("../pages/admin/AdminBundlesPage").then((m) => ({ default: m.AdminBundlesPage })));
const AdminCustomersPage = lazy(() => import("../pages/admin/AdminCustomersPage").then((m) => ({ default: m.AdminCustomersPage })));
const AdminSettingsPage = lazy(() => import("../pages/admin/AdminSettingsPage").then((m) => ({ default: m.AdminSettingsPage })));

function AdminFallback() {
  return <div className="min-h-screen flex items-center justify-center text-ink-muted">Loading admin…</div>;
}

export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    errorElement: <ErrorPage />,
    children: [
      { path: "/", element: <HomePage /> },
      { path: "/login", element: <LoginPage /> },
      { path: "/register", element: <RegisterPage /> },
      { path: "/category/:slug", element: <CategoryPage /> },
      { path: "/search", element: <SearchPage /> },
      { path: "/product/:slug", element: <ProductPage /> },
      {
        path: "/about",
        element: (
          <LazyPage>
            <AboutPage />
          </LazyPage>
        )
      },
      {
        path: "/returns",
        element: (
          <LazyPage>
            <ReturnsPage />
          </LazyPage>
        )
      },
      {
        path: "/contact",
        element: (
          <LazyPage>
            <ContactPage />
          </LazyPage>
        )
      },
      {
        path: "/privacy",
        element: (
          <LazyPage>
            <PrivacyPolicyPage />
          </LazyPage>
        )
      },
      {
        path: "/terms",
        element: (
          <LazyPage>
            <TermsPage />
          </LazyPage>
        )
      },
      {
        path: "/track",
        element: (
          <LazyPage>
            <TrackOrderPage />
          </LazyPage>
        )
      },
      {
        path: "/offers",
        element: (
          <LazyPage>
            <OffersPage />
          </LazyPage>
        )
      },
      {
        path: "/bundles",
        element: (
          <LazyPage>
            <BundlesPage />
          </LazyPage>
        )
      },
      {
        path: "/bundles/:slug",
        element: (
          <LazyPage>
            <BundleDetailPage />
          </LazyPage>
        )
      },
      {
        path: "/cart",
        element: (
          <ProtectedRoute>
            <CartPage />
          </ProtectedRoute>
        )
      },
      {
        path: "/checkout",
        element: (
          <ProtectedRoute>
            <CheckoutPage />
          </ProtectedRoute>
        )
      },
      {
        path: "/payment/safepay/success",
        element: <ProtectedRoute><SafepayResultPage /></ProtectedRoute>
      },
      {
        path: "/payment/safepay/cancel",
        element: <ProtectedRoute><SafepayResultPage cancelled /></ProtectedRoute>
      },
      {
        path: "/account",
        element: (
          <ProtectedRoute>
            <AccountLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <AccountPage /> },
          { path: "orders", element: <OrdersPage /> },
          { path: "orders/:id", element: <OrderDetailsPage /> },
          { path: "wishlist", element: <WishlistPage /> },
          { path: "wallet", element: <WalletPage /> },
          { path: "settings", element: <SettingsPage /> }
        ]
      },
      {
        path: "*",
        element: (
          <LazyPage>
            <NotFoundPage />
          </LazyPage>
        )
      }
    ]
  },
  {
    path: "/admin",
    element: (
      <ProtectedRoute role={["ADMIN", "SUPER_ADMIN"]}>
        <Suspense fallback={<AdminFallback />}>
          <AdminShell />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <AdminOverviewPage /> },
      { path: "products", element: <AdminProductsPage /> },
      { path: "inventory", element: <AdminInventoryPage /> },
      { path: "categories", element: <AdminCategoriesPage /> },
      { path: "orders", element: <AdminOrdersPage /> },
      { path: "orders/:id/invoice", element: <AdminInvoicePage /> },
      { path: "returns", element: <AdminReturnsPage /> },
      { path: "discounts", element: <AdminDiscountsPage /> },
      { path: "reviews", element: <AdminReviewsPage /> },
      { path: "bundles", element: <AdminBundlesPage /> },
      { path: "customers", element: <AdminCustomersPage /> },
      { path: "analytics", element: <AdminOverviewPage /> },
      { path: "reports", element: <AdminBusinessReportPage /> },
      { path: "settings", element: <AdminSettingsPage /> }
    ]
  }
]);
